import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
  SvgIconProps,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../providers/ModalProvider';
import { useTheme } from '../../providers/ThemeProvider';
import { primary, secondary } from '../../Constants';

// Constants for nav height - used for safe area calculations
export const NAV_HEIGHT = 64;

// Config-driven navigation items for easier maintenance
interface NavItem {
  value: string;
  label: string;
  icon: React.ComponentType<SvgIconProps>;
  path: string;
  pathMatches: (pathname: string) => boolean;
  requiresAuth: boolean;
  showBadge?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    value: 'home',
    label: 'Home',
    icon: HomeIcon,
    path: '/home',
    pathMatches: (p) => p === '/home' || p === '/',
    requiresAuth: false,
  },
  {
    value: 'search',
    label: 'Recipes',
    icon: SearchIcon,
    path: '/recipes',
    pathMatches: (p) => p === '/recipes',
    requiresAuth: false,
  },
  {
    value: 'favorites',
    label: 'Favorites',
    icon: FavoriteIcon,
    path: '/myFavorites',
    pathMatches: (p) => p === '/myFavorites',
    requiresAuth: true,
    showBadge: true,
  },
  {
    value: 'profile',
    label: 'Profile',
    icon: PersonIcon,
    path: '/me',
    pathMatches: (p) => p.includes('/me') || p.includes('/user'),
    requiresAuth: true,
  },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { setShowLoginModal } = useModal();
  const { colors } = useTheme();
  const [value, setValue] = useState('home');

  const favoritesCount = currentUser?.favorites?.length || 0;

  // Update selected value based on current route using config
  useEffect(() => {
    const matchedItem = NAV_ITEMS.find((item) => item.pathMatches(location.pathname));
    setValue(matchedItem?.value || '');
  }, [location.pathname]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    const navItem = NAV_ITEMS.find((item) => item.value === newValue);
    if (!navItem) return;

    setValue(newValue);

    // Check if auth is required and user is not logged in
    if (navItem.requiresAuth && !currentUser) {
      setShowLoginModal(true);
      return;
    }

    navigate(navItem.path);
  };

  // Render icon with optional badge
  const renderIcon = (item: NavItem) => {
    const IconComponent = item.icon;

    if (item.showBadge && favoritesCount > 0) {
      return (
        <Badge
          badgeContent={favoritesCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: secondary,
              fontSize: '0.65rem',
              minWidth: 16,
              height: 16,
            },
          }}
        >
          <IconComponent />
        </Badge>
      );
    }

    return <IconComponent />;
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: { xs: 'block', md: 'none' },
        zIndex: 9999,
        borderTop: `1px solid ${colors.border}`,
        backgroundColor: colors.navBackground,
        // iOS safe area support for devices with home indicator
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      elevation={3}
    >
      <BottomNavigation
        value={value}
        onChange={handleChange}
        sx={{
          backgroundColor: colors.navBackground,
          height: NAV_HEIGHT,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 0',
            color: colors.textSecondary,
            '&.Mui-selected': {
              color: primary,
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.7rem',
            '&.Mui-selected': {
              fontSize: '0.75rem',
              fontWeight: 600,
            },
          },
        }}
      >
        {NAV_ITEMS.map((item) => (
          <BottomNavigationAction
            key={item.value}
            label={item.label}
            value={item.value}
            icon={renderIcon(item)}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
}
